import { OrderCardHeader } from '@src/components/OMS/OMSCard/OrderCardHeader';
import { OrderCardSummary } from '@src/components/OMS/OMSCard/OrderCardSummary';
import { useOrders } from '@src/providers';
import {
  ACTION,
  Dialog,
  Divider,
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  OrdTypeEnum,
  Text,
  VStack,
  abbreviateId,
  getSymbolsAcrossMarkets,
  isOrderComplete,
  isOrderPaused,
  useMixpanel,
  useParameterRows,
  useSecurity,
  useStrategiesContext,
  useUserContext,
  type DialogProps,
  type ExecutionReport,
  type Order,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { hideFeedbackCard } from '../../../../components/OMS/OMSSlice';
import { useRoleAuth } from '../../../../hooks';
import { useAppStateDispatch } from '../../../../providers/AppStateProvider';
import { SelectedOrdersList } from './SelectedOrdersList';
import { SelectedOrdersUsers } from './SelectedOrdersUsers';
import { ResumeDialogWrapper } from './styles';

interface BulkResumeDialogProps {
  selectedOrders?: Order[];
  resumeDialog: DialogProps;
}

export const BulkResumeDialog = ({ selectedOrders, resumeDialog }: BulkResumeDialogProps) => {
  if (!selectedOrders || selectedOrders.length === 0) {
    return null;
  }

  if (selectedOrders.length === 1) {
    return <ResumeDialog resumeDialog={resumeDialog} selectedOrder={selectedOrders[0]} />;
  }

  return <ResumeMultipleDialog resumeDialog={resumeDialog} selectedOrders={selectedOrders} />;
};

interface ResumeDialogProps {
  selectedOrder?: Order | ExecutionReport;
  resumeDialog: DialogProps;
}

export const ResumeDialog = ({ selectedOrder, resumeDialog }: ResumeDialogProps) => {
  const { isAuthorized } = useRoleAuth();
  const mixpanel = useMixpanel();
  const orders = useOrders();
  const security = useSecurity(selectedOrder?.Symbol);
  const { strategiesByName: strats } = useStrategiesContext();
  const paramRows = useParameterRows(selectedOrder?.Strategy, selectedOrder?.Parameters);
  const dispatch = useAppStateDispatch();

  if (selectedOrder == null || security == null) {
    return null;
  }

  const paused = isOrderPaused(selectedOrder.DecisionStatus);
  const underlyingSymbols = getSymbolsAcrossMarkets(selectedOrder);
  const showFees = selectedOrder.OrdType === OrdTypeEnum.LimitAllIn;

  const selectedStrategy = selectedOrder.Strategy ? strats?.get(selectedOrder.Strategy) : undefined;

  return (
    <Dialog
      {...resumeDialog}
      width={320}
      onConfirm={() => {
        dispatch(hideFeedbackCard());
        if (selectedOrder) {
          mixpanel.track(MixpanelEvent.ResumeOrder, { [MixpanelEventProperty.ID]: selectedOrder.OrderID });
          orders.resume(selectedOrder.OrderID);
        }
      }}
      confirmLabel="Resume Order"
      cancelLabel="Return"
      confirmDisabled={!paused || !isAuthorized(ACTION.RESUME_ORDER)}
      title="Confirm Resumption"
      stretchButtons
    >
      <ResumeDialogWrapper>
        <VStack gap="spacing16" alignItems="stretch">
          <h3 style={{ textAlign: 'left' }}>
            Order #<b>{abbreviateId(selectedOrder.OrderID)}</b>
          </h3>
          <Text style={{ textAlign: 'left' }} color="colorTextAttention">
            Are you sure you want to resume this order?
          </Text>
          <Divider />
          <VStack gap="spacing8" alignItems="stretch">
            <OrderCardHeader
              order={selectedOrder}
              selectedStrategy={selectedStrategy}
              underlyingSymbols={underlyingSymbols}
            />
            <Divider />
            <OrderCardSummary security={security} showFees={showFees} {...selectedOrder} customerOrder={undefined} />
          </VStack>
          {paramRows.length > 0 && (
            <>
              <Divider />
              <VStack gap="spacing8" alignItems="stretch">
                {paramRows.map(row => {
                  return (
                    <HStack justifyContent="space-between" key={row.title}>
                      <Text color="colorTextAttention">{row.title}</Text>
                      <Text>{row.value}</Text>
                    </HStack>
                  );
                })}
              </VStack>
            </>
          )}
        </VStack>
      </ResumeDialogWrapper>
    </Dialog>
  );
};

const ResumeMultipleDialog = ({ selectedOrders, resumeDialog }: Required<BulkResumeDialogProps>) => {
  const { isAuthorized } = useRoleAuth();
  const mixpanel = useMixpanel();
  const { resume } = useOrders();
  const { user } = useUserContext();
  const onHandleResume = useCallback(
    () =>
      selectedOrders.forEach(selectedOrder => {
        mixpanel.track(MixpanelEvent.ResumeOrder, { [MixpanelEventProperty.ID]: selectedOrder.OrderID });
        resume(selectedOrder.OrderID);
      }),
    [selectedOrders, mixpanel, resume]
  );

  return (
    <Dialog
      {...resumeDialog}
      onConfirm={onHandleResume}
      confirmLabel="Resume Orders"
      cancelLabel="Return"
      confirmDisabled={
        selectedOrders.every(selectedOrder => isOrderComplete(selectedOrder.OrdStatus)) ||
        !isAuthorized(ACTION.RESUME_ORDER)
      }
      title="Confirm Resumption"
    >
      <ResumeDialogWrapper>
        <VStack gap="spacing8" alignItems="stretch">
          {selectedOrders.every(selectedOrder => selectedOrder.User === user?.Name) ? (
            <Text style={{ textAlign: 'left' }} color="colorTextAttention">
              Are you sure you want to resume these {selectedOrders.length} orders?
            </Text>
          ) : (
            <Text style={{ textAlign: 'left' }} color="colorTextAttention">
              You are about to resume {selectedOrders.length} orders belonging to{' '}
              <SelectedOrdersUsers selectedOrders={selectedOrders} />
              .<br />
              Are you sure you want to proceed?
            </Text>
          )}
          <Divider />
          <SelectedOrdersList selectedOrders={selectedOrders} />
        </VStack>
      </ResumeDialogWrapper>
    </Dialog>
  );
};
